import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  TextField,
  Grid,
  FormControl,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Paper,
  Divider,
  alpha,
} from "@mui/material";
import {
  Inventory2,
  Devices,
  Save,
  ArrowBack,
  Info,
  WaterDrop,
  ElectricBolt,
  Tv,
  DevicesOther,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { postData } from "../API/Post";
import updateSnackbarMessage from "../functions/updateSnackbarMessage";
import { putData } from "../API/Put";
import fetchPacks from "../functions/fetchPacks";
import { useEffect, useState } from "react";
import fetchData from "../API/Fetch";
import Autocomplete from "@mui/material/Autocomplete";
import logger from "../utils/logger";

// Icône selon le type de device
const getDeviceIcon = (deviceType) => {
  const type = deviceType?.toLowerCase() || "";
  if (type.includes("water") || type.includes("eau")) {
    return <WaterDrop sx={{ fontSize: 16 }} />;
  }
  if (type.includes("elec") || type.includes("power") || type.includes("énergie")) {
    return <ElectricBolt sx={{ fontSize: 16 }} />;
  }
  if (type.includes("screen") || type.includes("écran") || type.includes("display")) {
    return <Tv sx={{ fontSize: 16 }} />;
  }
  return <DevicesOther sx={{ fontSize: 16 }} />;
};

// Couleur selon le type de device (tons sobres)
const getDeviceColor = (deviceType) => {
  const type = deviceType?.toLowerCase() || "";
  if (type.includes("water") || type.includes("eau")) return "#6b9dad";
  if (type.includes("elec") || type.includes("power") || type.includes("énergie")) return "#c4a574";
  if (type.includes("screen") || type.includes("écran") || type.includes("display")) return "#8a9ba8";
  return "#7a8a7a";
};

function PackUpdate() {
  const devices = useSelector((state) => state.devices);
  const deviceBrands = useSelector((state) => state.deviceBrands);

  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation("common");

  const [pack, setPack] = useState(null);
  const [selectedDevices, setSelectedDevices] = useState([]);

  useEffect(() => {
    if (!params.id) {
      return;
    }

    fetchData(`/packs/${params.id}`).then((data) => {
      setPack(data);
      logger.log("Fetched pack data:", data);
      if (data && data.devices) {
        // Normalize devices to dev_eui string array
        const devEuiList = data.devices.map((d) =>
          typeof d === "string" ? d : d.dev_eui
        );
        logger.log("Selected devices:", devEuiList);
        setSelectedDevices(devEuiList);
      }
    });

    store.dispatch({
      type: "pageActions/update",
      payload: [],
    });

    const pageTitle = params.id
      ? `${t("common.update")} ${t("common.pack")}`
      : t("common.add_new", { name: t("common.pack") });
    store.dispatch({
      type: "pageTitle/update",
      payload: pageTitle,
    });
  }, [params.id, t]);

  const { register, handleSubmit, control, reset } = useForm({
    defaultValues: {
      id: "",
    },
  });

  useEffect(() => {
    if (!pack) {
      return;
    }

    const { id } = pack;

    reset({
      id: id,
    });
  }, [pack, reset]);

  const validationLabels = {
    id: {
      required: t("common.required", { name: t("common.id") }),
    },
  };

  const onSubmit = async (data) => {
    let { id } = data;

    let jsonData = {
      id: id,
      devices: selectedDevices,
    };

    let response = null;

    if (pack !== null) {
      response = await putData(`/packs/${pack.id}`, jsonData);
    } else {
      response = await postData(`/packs/`, jsonData);
    }

    if (response && response.status === 200) {
      updateSnackbarMessage(
        pack
          ? t("common.updated_success", { name: t("common.pack") })
          : t("common.added_success", { name: t("common.pack") })
      );

      fetchPacks();
      navigate("/packs/");
    } else {
      updateSnackbarMessage(
        pack
          ? t("common.update_error", { name: t("common.pack") })
          : t("common.add_error", { name: t("common.pack") })
      );
    }
  };

  const availableDevices = devices.filter(
    (device) => !selectedDevices.includes(device.dev_eui)
  );

  return (
    <Grid container spacing={3}>
      {/* Section Information du Pack */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha("#78a882", 0.15)}`,
            background: alpha("#78a882", 0.03),
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: alpha("#78a882", 0.1),
                display: "flex",
              }}
            >
              <Inventory2 sx={{ color: "#78a882" }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("common.information") || "Informations"}
            </Typography>
          </Box>

          <Controller
            name="id"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth variant="outlined">
                <TextField
                  id="id"
                  label={t("common.id")}
                  fullWidth
                  {...field}
                  error={error !== undefined}
                  helperText={error ? validationLabels.id[error.type] : ""}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  {...register("id")}
                />
              </FormControl>
            )}
          />

          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha("#6b9dad", 0.06),
              border: `1px solid ${alpha("#6b9dad", 0.1)}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Info sx={{ color: "#6b9dad", fontSize: 20, mt: 0.2 }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("common.pack_info_hint") ||
                "L'identifiant du pack doit être unique. Il sera utilisé pour associer les appareils à une résidence."}
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* Section Devices */}
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${alpha("#8a9ba8", 0.15)}`,
            background: alpha("#8a9ba8", 0.03),
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: alpha("#8a9ba8", 0.1),
                  display: "flex",
                }}
              >
                <Devices sx={{ color: "#8a9ba8" }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("common.devices")}
              </Typography>
            </Box>
            <Chip
              label={`${selectedDevices.length} ${t("common.selected") || "sélectionné(s)"}`}
              size="small"
              sx={{
                backgroundColor: alpha("#8a9ba8", 0.12),
                color: "#8a9ba8",
                fontWeight: 600,
              }}
            />
          </Box>

          <Autocomplete
            multiple
            options={availableDevices}
            getOptionLabel={(option) => {
              const brand = deviceBrands.find(
                (db) => db.id === option.device_brand_id
              );
              return `${option.name} (${option.dev_eui})${brand ? ` [${brand.name}]` : ""}`;
            }}
            value={selectedDevices
              .map((devEui) => devices.find((d) => d.dev_eui === devEui))
              .filter(Boolean)}
            onChange={(_event, value) => {
              setSelectedDevices(value.map((v) => v.dev_eui));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const deviceType =
                  option?.device_brand?.device_type?.name ||
                  option?.device_type?.name ||
                  "";
                const color = getDeviceColor(deviceType);
                return (
                  <Chip
                    icon={getDeviceIcon(deviceType)}
                    label={`${option.name}`}
                    {...getTagProps({ index })}
                    sx={{
                      borderColor: alpha(color, 0.5),
                      color: color,
                      backgroundColor: alpha(color, 0.05),
                      "& .MuiChip-icon": { color: color },
                      "& .MuiChip-deleteIcon": {
                        color: alpha(color, 0.7),
                        "&:hover": { color: color },
                      },
                    }}
                    variant="outlined"
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label={t("common.search_device") || "Rechercher un appareil"}
                placeholder={t("common.search_device") || "Rechercher..."}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.dev_eui === value.dev_eui
            }
            sx={{ width: "100%" }}
            disableCloseOnSelect
          />

          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.5, color: "text.secondary" }}
          >
            {availableDevices.length} {t("common.devices").toLowerCase()}{" "}
            {t("common.available") || "disponible(s)"}
          </Typography>
        </Paper>
      </Grid>

      {/* Barre d'actions */}
      <Grid item xs={12}>
        <Divider sx={{ mb: 3 }} />
        <Stack
          spacing={2}
          direction="row"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate("/packs")}
            sx={{
              borderColor: alpha("#7a8a7a", 0.4),
              color: "#9aa89a",
              "&:hover": {
                borderColor: "#7a8a7a",
                backgroundColor: alpha("#7a8a7a", 0.08),
              },
            }}
          >
            {t("common.cancel") || "Annuler"}
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            sx={{
              backgroundColor: "#5a7a5e",
              "&:hover": {
                backgroundColor: "#4a6a4e",
              },
            }}
          >
            {pack && pack.id ? t("common.update") : t("common.add")}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(PackUpdate);
